import * as React from 'react'
import clsx from 'clsx'
import * as plume from '@ulule/owl-kit-components/next'

import { Container } from '../../_shared/Container'
import { PROFILES, type Profile, type ProfileIcon, type ProfileVariant } from '../content'
import styles from './Profiles.module.css'

const VARIANT_CLASS: Record<ProfileVariant, string> = {
  blue: styles.accBlue,
  yellow: styles.accYellow,
  green: styles.accGreen,
  red: styles.accRed,
}

function ProfileGlyph({ icon }: { icon: ProfileIcon }): React.ReactElement {
  switch (icon) {
    case 'people':
      return <plume.glyphs.stroke.People size={24} />
    case 'megaphone':
      return <plume.glyphs.stroke.Megaphone size={24} />
    case 'heart':
      return <plume.glyphs.stroke.Heart size={24} />
    case 'edit':
      return <plume.glyphs.stroke.Edit size={24} />
  }
}

function AccordionItem({ profile }: { profile: Profile }): React.ReactElement {
  // Deterministic initial state (collapsed unless the profile opts in) so SSR and hydration agree.
  const [open, setOpen] = React.useState(profile.defaultOpen ?? false)
  const panelId = React.useId()

  return (
    <div className={clsx(styles.acc, VARIANT_CLASS[profile.variant], open && styles.accIsOpen)}>
      <button
        type="button"
        className={styles.acc__head}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={styles.acc__icon} aria-hidden="true">
          <ProfileGlyph icon={profile.icon} />
        </span>
        <span className={styles.acc__title}>{profile.title}</span>
        <span className={styles.acc__chev} aria-hidden="true">
          <plume.glyphs.stroke.ArrowRight size={16} />
        </span>
      </button>
      <div className={styles.acc__panel} id={panelId}>
        <div className={styles.acc__body}>
          <p className={styles.acc__text}>{profile.body}</p>
          <div className={styles.acc__cta}>
            <plume.RoundButton as="a" href={profile.primaryCta.href} color="yellow" size="small">
              {profile.primaryCta.label}
            </plume.RoundButton>
            {profile.secondaryLink !== undefined && (
              <a className={styles.acc__seclink} href={profile.secondaryLink.href}>
                {profile.secondaryLink.label} <plume.glyphs.stroke.ArrowRight size={13} />
              </a>
            )}
          </div>
          {profile.faq !== undefined && (
            <div className={styles.acc__faq}>
              <span className={styles.acc__faqLabel}>Questions fréquentes :</span>
              {profile.faq.map((link) => (
                <a key={link.href} className={styles.acc__faqLink} href={link.href}>
                  {link.label} <plume.glyphs.stroke.ArrowRight size={11} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function Profiles(): React.ReactElement {
  return (
    <section className={styles.section} id="objet">
      <Container>
        <div className={styles.section__head}>
          <h2 className={styles.section__title}>Quel est l'objet de votre demande&nbsp;?</h2>
          <p className={styles.section__lede}>
            Sélectionnez votre profil pour être orienté vers le bon interlocuteur.
          </p>
        </div>
        <div className={styles.list}>
          {PROFILES.map((profile) => (
            <AccordionItem key={profile.title} profile={profile} />
          ))}
        </div>
      </Container>
    </section>
  )
}
